/**
 * /admin/billing-automation — Platform Billing Administration
 *
 * Tabs:
 *   Mandate Overview — all tenant mandates with status
 *   Platform Defaults — auto-recharge thresholds, subscription defaults
 *   Recharge Log — recent auto-recharge attempts
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";
import {
    CreditCard, RefreshCw, Shield, CheckCircle2, AlertTriangle,
    Clock, XCircle, Settings, TrendingDown, List, ToggleLeft, ToggleRight,
} from "lucide-react";

const fmtINR = (n) =>
    "₹" + (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit", timeZone: "Asia/Kolkata" }) : "—");

function Field({ label, children, hint }) {
    return (
        <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</label>
            {children}
            {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
        </div>
    );
}

function StatusBadge({ status }) {
    if (status === "active")
        return <span className="text-[10px] uppercase tracking-wide text-emerald-400 border border-emerald-400/30 px-1.5 py-0.5 bg-emerald-400/5">Active</span>;
    if (status === "pending")
        return <span className="text-[10px] uppercase tracking-wide text-amber-400 border border-amber-400/30 px-1.5 py-0.5 bg-amber-400/5">Pending</span>;
    if (status === "cancelled")
        return <span className="text-[10px] uppercase tracking-wide text-rose-400 border border-rose-400/30 px-1.5 py-0.5 bg-rose-400/5">Cancelled</span>;
    return <span className="text-[10px] uppercase tracking-wide text-muted-foreground border border-border px-1.5 py-0.5">None</span>;
}

// ─── Mandate Overview Tab ────────────────────────────────────────────────────
function MandateOverviewTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // all | active | none

    const load = useCallback(() => {
        setLoading(true);
        api.get("/admin/billing/mandates")
            .then(r => setData(r.data))
            .catch(e => toast.error(apiError(e)))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const rows = data?.mandates || [];
    const filtered = filter === "all" ? rows
        : filter === "active" ? rows.filter(r => r.mandate_status === "active")
        : rows.filter(r => r.mandate_status === "none" || !r.mandate_status);

    const counts = {
        active: rows.filter(r => r.mandate_status === "active").length,
        pending: rows.filter(r => r.mandate_status === "pending").length,
        none: rows.filter(r => !r.mandate_status || r.mandate_status === "none").length,
        ar: rows.filter(r => r.auto_recharge_enabled).length,
    };

    return (
        <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                    { label: "Active mandates", value: counts.active, icon: CheckCircle2, color: "text-emerald-400" },
                    { label: "Pending", value: counts.pending, icon: Clock, color: "text-amber-400" },
                    { label: "No mandate", value: counts.none, icon: XCircle, color: "text-rose-400" },
                    { label: "Auto-recharge ON", value: counts.ar, icon: TrendingDown, color: "text-primary" },
                ].map(c => (
                    <div key={c.label} className="border border-border bg-card p-4 space-y-2">
                        <c.icon className={`w-4 h-4 ${c.color}`}/>
                        <div className={`text-2xl font-mono ${c.color}`}>{c.value}</div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{c.label}</div>
                    </div>
                ))}
            </div>

            {/* Filter tabs */}
            <div className="flex gap-1">
                {[["all", "All"], ["active", "Active only"], ["none", "No mandate"]].map(([v, l]) => (
                    <button key={v} onClick={() => setFilter(v)}
                        data-testid={`mandate-filter-${v}`}
                        className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border transition-colors ${
                            filter === v ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:border-primary/50"
                        }`}>
                        {l}
                    </button>
                ))}
                <button onClick={load} className="ml-auto px-3 py-1.5 text-muted-foreground hover:text-foreground">
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}/>
                </button>
            </div>

            {/* Table */}
            <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border">
                        <tr>
                            <th className="text-left px-3 py-2">Firm</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-left px-3 py-2">Type</th>
                            <th className="text-left px-3 py-2">Token saved</th>
                            <th className="text-right px-3 py-2">Auto-recharge</th>
                            <th className="text-right px-3 py-2">Threshold</th>
                            <th className="text-right px-3 py-2">Amount</th>
                            <th className="text-left px-3 py-2">Set up on</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
                        )}
                        {!loading && filtered.length === 0 && (
                            <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No mandates found</td></tr>
                        )}
                        {!loading && filtered.map(r => (
                            <tr key={r.tenant_id} className="border-b border-card hover:bg-card/70">
                                <td className="px-3 py-2 text-foreground font-medium">
                                    {r.tenant_name}
                                    <div className="text-[10px] text-muted-foreground font-normal">{r.tenant_slug}</div>
                                </td>
                                <td className="px-3 py-2"><StatusBadge status={r.mandate_status}/></td>
                                <td className="px-3 py-2 text-muted-foreground uppercase text-[10px]">{r.mandate_type || "—"}</td>
                                <td className="px-3 py-2">
                                    {r.razorpay_token_id
                                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400"/>
                                        : <XCircle className="w-3.5 h-3.5 text-muted-foreground"/>}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {r.auto_recharge_enabled
                                        ? <span className="text-emerald-400 text-[10px] uppercase">On</span>
                                        : <span className="text-muted-foreground text-[10px] uppercase">Off</span>}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-muted-foreground">{fmtINR(r.recharge_threshold)}</td>
                                <td className="px-3 py-2 text-right font-mono text-foreground">{fmtINR(r.recharge_amount)}</td>
                                <td className="px-3 py-2 text-muted-foreground">{fmtDate(r.mandate_setup_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Platform Defaults Tab ───────────────────────────────────────────────────
function PlatformDefaultsTab() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/billing/defaults")
            .then(r => setCfg(r.data))
            .catch(e => toast.error(apiError(e)));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const updated = await api.patch("/admin/billing/defaults", {
                auto_recharge_threshold_default: parseFloat(cfg.auto_recharge_threshold_default) || 100,
                auto_recharge_amount_default: parseFloat(cfg.auto_recharge_amount_default) || 500,
                daily_cap_default: parseFloat(cfg.daily_cap_default) || 5000,
                monthly_cap_default: parseFloat(cfg.monthly_cap_default) || 20000,
                subscription_auto_renewal_default: Boolean(cfg.subscription_auto_renewal_default),
                mandate_setup_required: Boolean(cfg.mandate_setup_required),
                mandate_auth_only_option: Boolean(cfg.mandate_auth_only_option),
            });
            setCfg(updated.data);
            toast.success("Platform billing defaults saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    if (!cfg) return <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>;

    const Toggle = ({ field, label, hint }) => (
        <div className="flex items-start justify-between py-3 border-b border-border last:border-0">
            <div>
                <div className="text-sm text-foreground">{label}</div>
                {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
            </div>
            <button type="button" onClick={() => setCfg({ ...cfg, [field]: !cfg[field] })}
                data-testid={`toggle-${field}`}
                className="ml-4 flex-shrink-0">
                {cfg[field]
                    ? <ToggleRight className="w-8 h-8 text-primary"/>
                    : <ToggleLeft className="w-8 h-8 text-muted-foreground"/>}
            </button>
        </div>
    );

    const NumField = ({ field, label, min, max, step = 1, hint }) => (
        <Field label={label} hint={hint}>
            <input type="number" value={cfg[field] || ""} min={min} max={max} step={step}
                onChange={e => setCfg({ ...cfg, [field]: e.target.value })}
                data-testid={`field-${field}`}
                className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
        </Field>
    );

    return (
        <div className="space-y-6 max-w-2xl">
            {/* Wallet Auto-recharge defaults */}
            <div className="border border-border bg-card p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-primary"/>
                    <h3 className="text-sm text-foreground">Wallet Auto-Recharge Defaults</h3>
                </div>
                <p className="text-xs text-muted-foreground">
                    These apply to new tenants that haven't set their own auto-recharge preferences yet.
                </p>
                <div className="grid grid-cols-2 gap-4">
                    <NumField field="auto_recharge_threshold_default" label="Trigger threshold (₹)" min={50} max={1000}
                        hint="Auto-recharge fires when wallet drops below this amount"/>
                    <NumField field="auto_recharge_amount_default" label="Recharge amount (₹)" min={250} max={5000}
                        hint="Amount credited on each auto-recharge (min ₹250)"/>
                    <NumField field="daily_cap_default" label="Daily cap (₹)" min={500} max={50000}
                        hint="Maximum auto-recharge per tenant per day"/>
                    <NumField field="monthly_cap_default" label="Monthly cap (₹)" min={1000} max={200000}
                        hint="Maximum auto-recharge per tenant per month"/>
                </div>
            </div>

            {/* Mandate settings */}
            <div className="border border-border bg-card p-5 space-y-1">
                <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4 text-primary"/>
                    <h3 className="text-sm text-foreground">Mandate Settings</h3>
                </div>
                <Toggle field="mandate_setup_required"
                    label="Require mandate on first login"
                    hint="If ON, tenants are prompted to set up a mandate before using the platform"/>
                <Toggle field="mandate_auth_only_option"
                    label="Allow ₹1 auth-only mandate"
                    hint="Lets tenants set up a mandate without an initial wallet top-up (₹1 is auto-refunded)"/>
            </div>

            {/* Subscription defaults */}
            <div className="border border-border bg-card p-5 space-y-1">
                <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-4 h-4 text-primary"/>
                    <h3 className="text-sm text-foreground">Subscription Defaults</h3>
                </div>
                <Toggle field="subscription_auto_renewal_default"
                    label="Auto-renewal ON by default"
                    hint="New tenants will have subscription auto-renewal enabled unless they opt out"/>
            </div>

            <button onClick={save} disabled={saving} data-testid="save-billing-defaults"
                className="px-5 py-2.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                {saving ? "Saving…" : "Save defaults"}
            </button>
        </div>
    );
}

// ─── Auto-Recharge Log Tab ───────────────────────────────────────────────────
function RechargeLogTab() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        api.get("/admin/billing/auto-recharge-log?limit=100")
            .then(r => setLogs(r.data?.logs || []))
            .catch(e => toast.error(apiError(e)))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const statusColor = (s) =>
        s === "success" ? "text-emerald-400" : s === "failed" ? "text-rose-400" : "text-amber-400";

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    Last {logs.length} auto-recharge attempts
                </div>
                <button onClick={load} data-testid="refresh-recharge-log"
                    className="px-3 py-1.5 text-muted-foreground hover:text-foreground">
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}/>
                </button>
            </div>
            <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border">
                        <tr>
                            <th className="text-left px-3 py-2">Tenant ID</th>
                            <th className="text-right px-3 py-2">Amount</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-left px-3 py-2">Error</th>
                            <th className="text-left px-3 py-2">Created at</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>}
                        {!loading && logs.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No auto-recharge events yet</td></tr>}
                        {!loading && logs.map((r, i) => (
                            <tr key={r.id || i} className="border-b border-card">
                                <td className="px-3 py-2 font-mono text-muted-foreground text-[10px]">{r.tenant_id?.slice(-8)}</td>
                                <td className="px-3 py-2 text-right font-mono text-foreground">{fmtINR(r.amount)}</td>
                                <td className={`px-3 py-2 uppercase text-[10px] ${statusColor(r.status)}`}>{r.status || "pending"}</td>
                                <td className="px-3 py-2 text-muted-foreground truncate max-w-[200px]">{r.error || "—"}</td>
                                <td className="px-3 py-2 text-muted-foreground">{fmtDate(r.created_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Main page ───────────────────────────────────────────────────────────────
const TABS = [
    { id: "mandates",  label: "Mandate Overview", icon: Shield },
    { id: "defaults",  label: "Platform Defaults", icon: Settings },
    { id: "log",       label: "Recharge Log", icon: List },
];

export default function AdminBillingAutomation() {
    const [tab, setTab] = useState("mandates");
    return (
        <div className="space-y-6" data-testid="admin-billing-automation-page">
            <PageHeader
                title="Billing Automation"
                subtitle="Platform-wide mandate overview, auto-recharge defaults, and subscription settings"
                icon={<CreditCard className="w-5 h-5 text-primary"/>}
            />

            {/* Tab bar */}
            <div className="flex gap-1 border-b border-border overflow-x-auto scrollbar-hide pb-0">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        data-testid={`billing-tab-${t.id}`}
                        className={`flex items-center gap-1.5 px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] shrink-0 transition-colors border-b-2 -mb-px ${
                            tab === t.id
                                ? "border-primary text-primary"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}>
                        <t.icon className="w-3.5 h-3.5"/>
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="pt-1">
                {tab === "mandates"  && <MandateOverviewTab/>}
                {tab === "defaults"  && <PlatformDefaultsTab/>}
                {tab === "log"       && <RechargeLogTab/>}
            </div>
        </div>
    );
}
